import { useSelector } from "react-redux";
import {
  Permission,
  ScopePermissions,
} from "../../features/home/types/menu-item";

export const useResourcePermissions = (resourceName?: string) => {
  const allPermissions = useSelector(
    (state: any) =>
      state?.session?.userDetails?.roles?.rolePermissions as Permission[]
  );

  function getScopesByResourceName(
    roles: Permission[],
    resourceName?: string
  ): ScopePermissions | null {
    if (!resourceName || !roles?.length) {
      return null;
    }

    for (const role of roles) {
      const match: any =
        role?.resource === resourceName && role?.scopes?.read
          ? role
          : role?.subresources?.find(
              (sub: any) => sub?.resource === resourceName
            );

      if (match) {
        return match.scopes;
      }
    }
    return null; // not found
  }

  const resourcePermissions: any = getScopesByResourceName(
    allPermissions,
    resourceName
  );

  const hasPermission = (permission: string): boolean => {
    const resourcePermission = getScopesByResourceName(
      allPermissions,
      resourceName
    );
    return resourcePermission?.[permission as keyof ScopePermissions] || false;
  };

  const hasAnyPermission = (resource: string, permission?: string) => {
    const resourcePermission = getScopesByResourceName(
      allPermissions,
      resource
    );

    if (permission) {
      return (
        resourcePermission?.[permission as keyof ScopePermissions] || false
      );
    }
    return resourcePermission;
  };

  const hasAllScopes = () =>
    !!resourcePermissions &&
    resourcePermissions.read &&
    resourcePermissions.write &&
    resourcePermissions.delete;

  return {
    permissions: resourcePermissions,
    hasPermission,
    hasAnyPermission,
    canRead: hasPermission("read"),
    canWrite: hasPermission("write"),
    canDelete: hasPermission("delete"),
    canImport: resourceName === "Import" && hasAllScopes(),
    canExport: resourceName === "Export" && hasAllScopes(),
  };
};
